/**
 * Subir base de datos (`/activa/publico/subir`, DISENO §3): Subir → Revisar → Listo. Del equipo.
 *
 * - Ventas y clientes se suben juntos y los dos son obligatorios (el diseño marcaba «Clientes» como opcional: no lo
 *   es). Cada archivo sube apenas se elige, de a partes (`logic/subida.ts`), y los dos pueden subir a la vez.
 * - La carga vive en el servidor: al entrar se retoma la que quedó sin terminar (`cargaAbierta`). Un archivo que
 *   quedó a medias sigue desde donde quedó si se vuelve a elegir el mismo.
 * - Leer y guardar corren en el servidor, en segundo plano: la pantalla consulta la carga hasta que terminan, y se
 *   puede salir y volver.
 * - «Confirmar y guardar» manda la huella de la revisión que se ve: si algo cambió, el backend no guarda (409).
 * - «Conectar tu sistema» queda como próximamente. Al terminar, «Listo» repite lo que se guardó y lleva a segmentar.
 * - En «Listo», si la versión anterior tenía el consentimiento declarado, pregunta si sigue valiendo (29/10/2026,
 *   `DeclaracionEnListo`): «Sí» lo hereda la versión nueva; «No» no declara nada.
 * - Arriba, la marca sobre la que se trabaja («Subiendo la base de HATSU»): para no cargarle la base a otro cliente.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Link2, Loader2, Upload } from "lucide-react";
import { baseVentasApi } from "../api";
import type { Archivo, CampoRevisado, Carga, EstadoBase } from "../types";
import { ARCHIVOS } from "../types";
import { ErrorSubida, avance, fundirParte, subirArchivo } from "../logic/subida";
import { archivosCompletos, conColumna, numerosResumen, pasoDe, type PasoSubida } from "../logic/revision";
import { ZonaArchivo, type ArchivoLocal } from "../components/ZonaArchivo";
import { ID_FRENOS, RevisionCarga } from "../components/RevisionCarga";
import { DeclaracionEnListo } from "../components/DeclaracionEnListo";
import { avisarCambioBase } from "../hooks/useBaseVieja";
import { llamar } from "../../wa-oficial/components/base/comun";
import { usePolling } from "../../wa-oficial/hooks/usePolling";
import { leerError, textoError } from "../../wa-oficial/logic/respuesta";
import { ErrorTarjeta, Esqueleto } from "../../wa-oficial/components/estadisticas/comun";
import { EncabezadoPagina, Pagina } from "../../../components/activa/Pagina";
import { R_NUEVA_CAMPANA } from "../../segmentaciones/logic/segmentar";
import { MarcaDeTrabajo } from "../../campana-nueva/components/MarcaDeTrabajo";

const R_PUBLICO = "/activa/publico";
/** Cada cuánto se mira la carga mientras el servidor lee o guarda. */
export const CADA_MS_CARGA = 2000;

const PASOS = ["Subir", "Revisar", "Listo"] as const;
const INDICE: Record<PasoSubida, number> = { subir: 0, leyendo: 0, revisar: 1, guardando: 1, listo: 2 };

const TEXTO_NO_ENCONTRADA = "No encontramos esa carga: puede haber vencido. Empezá de nuevo.";

const btnPrimario = "min-h-[44px] px-5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed";
const btnSecundario = "min-h-[44px] px-5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-800 hover:bg-slate-50 inline-flex items-center justify-center gap-2 disabled:opacity-50";

function Pasos({ actual }: { actual: number }) {
  return (
    <ol aria-label="Pasos" className="flex flex-wrap gap-2">
      {PASOS.map((p, i) => (
        <li key={p} aria-current={i === actual ? "step" : undefined}
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold ${
            i === actual ? "bg-violet-600 text-white" : i < actual ? "bg-violet-50 text-violet-700" : "bg-slate-100 text-slate-500"}`}>
          <span className="font-mono text-xs">{`0${i + 1}`}</span>{p}
        </li>
      ))}
    </ol>
  );
}

function Encabezado({ antetitulo, titulo, children }: { antetitulo: string; titulo: string; children?: React.ReactNode }) {
  return <EncabezadoPagina antetitulo={antetitulo} titulo={titulo} bajada={children} />;
}

export default function SubirBasePage() {
  const [inicio, setInicio] = useState<{ cargando: boolean; error: string; hayBase: boolean }>({ cargando: true, error: "", hayBase: false });
  const [carga, setCarga] = useState<Carga | null>(null);
  const [locales, setLocales] = useState<Partial<Record<Archivo, ArchivoLocal>>>({});
  const [verSubir, setVerSubir] = useState(false);
  const [ocupado, setOcupado] = useState<"" | "leyendo" | "revisando" | "confirmando" | "descartando">("");
  const [aviso, setAviso] = useState("");

  const cargaRef = useRef(carga);
  cargaRef.current = carga;
  /** el borrador que se está creando: si se eligen los dos archivos juntos, los dos esperan el mismo */
  const creando = useRef<Promise<Carga | null> | null>(null);
  /** la subida en curso de cada archivo, para cortarla si se elige otro (o se sale de la pantalla) */
  const subidas = useRef<Partial<Record<Archivo, { cortada: boolean; ctrl: AbortController }>>>({});
  /** el último archivo elegido de cada tipo, para «Reintentar» sin volver a buscarlo */
  const ultimoArchivo = useRef<Partial<Record<Archivo, File>>>({});

  const cortarSubidas = useCallback(() => {
    for (const s of Object.values(subidas.current)) {
      if (!s) continue;
      s.cortada = true;
      s.ctrl.abort();
    }
    subidas.current = {};
  }, []);

  const traerEstado = useCallback(async () => {
    setInicio((x) => ({ ...x, cargando: true, error: "" }));
    const l = await llamar<EstadoBase>(() => baseVentasApi.estado());
    if (!l.ok) {
      setInicio({ cargando: false, error: textoError(l), hayBase: false });
      return;
    }
    setCarga(l.data.cargaAbierta);
    setInicio({ cargando: false, error: "", hayBase: !!l.data.enUso });
  }, []);

  // al salir de la pantalla se corta lo que se estaba subiendo: al volver, elegir el mismo archivo sigue desde ahí
  useEffect(() => {
    void traerEstado();
    return cortarSubidas;
  }, [traerEstado, cortarSubidas]);

  const paso = pasoDe(carga);
  // «Atrás» desde Revisar vuelve a Subir sin perder la revisión; cualquier otro cambio de estado lo olvida
  useEffect(() => {
    if (carga?.estado !== "para_revisar") setVerSubir(false);
  }, [carga?.estado]);
  const pasoVisible: PasoSubida = verSubir && paso === "revisar" ? "subir" : paso;

  // el menú tiene el punto de «base desactualizada»: que se entere en el momento de la versión nueva
  useEffect(() => {
    if (carga?.estado === "guardada") avisarCambioBase();
  }, [carga?.estado]);

  const perdida = useCallback(() => {
    setCarga(null);
    setLocales({});
    setAviso(TEXTO_NO_ENCONTRADA);
  }, []);

  // mientras el servidor lee o guarda, se mira la carga
  const enServidor = paso === "leyendo" || paso === "guardando";
  const polling = usePolling(
    async () => {
      const c = cargaRef.current;
      if (!c) return;
      const l = await llamar<Carga>(() => baseVentasApi.verCarga(c.id));
      if (!l.ok) {
        if (l.status === 404) return perdida();
        throw new Error(textoError(l));
      }
      setCarga(l.data);
    },
    enServidor && carga ? CADA_MS_CARGA : null,
    [carga?.id],
  );

  const borrador = async (): Promise<Carga | null> => {
    const c = cargaRef.current;
    if (c && c.estado !== "guardada") return c;
    if (!creando.current) {
      creando.current = llamar<Carga>(() => baseVentasApi.crearCarga())
        .then((l) => {
          if (!l.ok) {
            setAviso(textoError(l));
            return null;
          }
          setCarga(l.data);
          cargaRef.current = l.data;
          return l.data;
        })
        .finally(() => { creando.current = null; });
    }
    return creando.current;
  };

  const elegir = async (tipo: Archivo, f: File | null | undefined) => {
    if (!f) return;
    const previa = subidas.current[tipo];
    if (previa) {
      previa.cortada = true;
      previa.ctrl.abort();
    }
    const yo = { cortada: false, ctrl: new AbortController() };
    subidas.current[tipo] = yo;
    ultimoArchivo.current[tipo] = f;
    const local = (cambios: Partial<ArchivoLocal>) => {
      if (yo.cortada) return;
      setLocales((p) => ({ ...p, [tipo]: { ...(p[tipo] ?? { nombre: f.name, subiendo: true, avance: 0, error: "" }), ...cambios } }));
    };
    setAviso("");
    local({ nombre: f.name, subiendo: true, avance: 0, error: "" });

    const c = await borrador();
    if (!c) return local({ subiendo: false, error: "No pudimos empezar la carga. Probá de nuevo." });
    try {
      const final = await subirArchivo(tipo, f, c.archivos[tipo], {
        subirParte: (p, cuerpo) => llamar<Carga>(() => baseVentasApi.subirParte(c.id, tipo, p, cuerpo, yo.ctrl.signal)),
        ver: () => llamar<Carga>(() => baseVentasApi.verCarga(c.id)),
        alAvanzar: (s, nueva) => {
          if (yo.cortada) return;
          setCarga((prev) => fundirParte(prev, nueva, tipo));
          local({ avance: avance(s) });
        },
        cortada: () => yo.cortada,
      });
      if (yo.cortada) return;
      setCarga((prev) => fundirParte(prev, final, tipo));
      local({ subiendo: false, avance: 100 });
    } catch (e) {
      if (yo.cortada) return;
      const err = e instanceof ErrorSubida ? e : null;
      if (err?.code === "NOT_FOUND") return perdida();
      local({ subiendo: false, error: err ? err.message : textoError(leerError(e)) });
    } finally {
      if (subidas.current[tipo] === yo) delete subidas.current[tipo];
    }
  };

  const continuar = async () => {
    if (!carga) return;
    setOcupado("leyendo");
    setAviso("");
    const l = await llamar<Carga>(() => baseVentasApi.leer(carga.id));
    setOcupado("");
    if (!l.ok) return l.status === 404 ? perdida() : setAviso(textoError(l));
    setVerSubir(false);
    setCarga(l.data);
  };

  const cambiarColumna = async (campo: CampoRevisado, valor: string) => {
    const rev = carga?.revision;
    if (!carga || !rev) return;
    const mapeo = conColumna(rev.mapeo, campo, valor);
    if (!mapeo) return;
    setOcupado("revisando");
    setAviso("");
    const l = await llamar<Carga>(() => baseVentasApi.revisar(carga.id, mapeo));
    setOcupado("");
    if (l.ok) return setCarga(l.data);
    if (l.status === 404) return perdida();
    setAviso(textoError(l));
  };

  const confirmar = async () => {
    const rev = carga?.revision;
    if (!carga || !rev) return;
    setOcupado("confirmando");
    setAviso("");
    const l = await llamar<Carga>(() => baseVentasApi.confirmar(carga.id, rev.huella));
    setOcupado("");
    if (l.ok) return setCarga(l.data);
    if (l.status === 404) return perdida();
    setAviso(textoError(l));
    // cambió lo que se revisó (otra pestaña): se muestra la revisión de ahora
    if (l.code === "HUELLA_DISTINTA") {
      const v = await llamar<Carga>(() => baseVentasApi.verCarga(carga.id));
      if (v.ok) setCarga(v.data);
    }
  };

  const reintentar = (tipo: Archivo) => {
    const f = ultimoArchivo.current[tipo];
    if (f) void elegir(tipo, f);
  };

  const empezarDeNuevo = async () => {
    if (!carga) return;
    cortarSubidas();
    setOcupado("descartando");
    setAviso("");
    const l = await llamar(() => baseVentasApi.descartar(carga.id));
    setOcupado("");
    if (!l.ok && l.status !== 404) return setAviso(textoError(l));
    setCarga(null);
    setLocales({});
  };

  const subiendo = ARCHIVOS.some((a) => locales[a]?.subiendo);
  const errorCarga = carga?.error?.texto || "";

  let cuerpo: React.ReactNode;
  if (inicio.cargando && !carga) {
    cuerpo = <Esqueleto alto="h-64" etiqueta="Cargando la base" />;
  } else if (inicio.error) {
    cuerpo = <ErrorTarjeta texto={inicio.error} onReintentar={() => void traerEstado()} />;
  } else if (pasoVisible === "leyendo" || pasoVisible === "guardando") {
    cuerpo = (
      <div role="status" className="rounded-2xl border border-slate-200 bg-white px-6 py-14 flex flex-col items-center gap-4 text-center">
        <Loader2 size={40} className="animate-spin text-violet-600" aria-hidden="true" />
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">{pasoVisible === "leyendo" ? "Leyendo tus archivos…" : "Guardando tu base…"}</h1>
        <p className="text-sm text-slate-600 max-w-md">Con archivos grandes puede tardar unos minutos. Si salís de esta pantalla, sigue sola y al volver la encontrás acá.</p>
        {polling.detenido && (
          <div role="alert" className="text-sm text-amber-800">
            Dejamos de mirar cómo va porque no pudimos conectar.{" "}
            <button type="button" onClick={polling.reintentar} className="font-semibold underline min-h-[44px]">Volver a mirar</button>
          </div>
        )}
      </div>
    );
  } else if (pasoVisible === "revisar" && carga?.revision) {
    const rev = carga.revision;
    const conFrenos = rev.frenos.length > 0;
    cuerpo = (
      <div className="space-y-5">
        <Encabezado antetitulo="Paso 2 de 3" titulo="¿Lo leímos bien?">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm text-slate-600">Mirá la última columna. Si algo no coincide, cambiá la columna.</p>
            {rev.mismoFormato && <span className="rounded-full bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700">Mismo formato que la vez pasada</span>}
          </div>
        </Encabezado>
        {errorCarga && <p role="alert" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{errorCarga}</p>}
        {ocupado === "revisando" && <p role="status" className="text-sm text-slate-600 inline-flex items-center gap-1.5"><Loader2 size={14} className="animate-spin" aria-hidden="true" /> Revisando con la columna nueva…</p>}
        <RevisionCarga revision={rev} lectura={carga.lectura} deshabilitada={!!ocupado} onColumna={(c, v) => void cambiarColumna(c, v)} />
        <div className="flex flex-wrap justify-between gap-3">
          <button type="button" onClick={() => setVerSubir(true)} disabled={!!ocupado} className={btnSecundario}>Atrás</button>
          <button type="button" onClick={() => void confirmar()} disabled={conFrenos || !!ocupado} aria-describedby={conFrenos ? ID_FRENOS : undefined} className={btnPrimario}>
            {ocupado === "confirmando" && <Loader2 size={15} className="animate-spin" aria-hidden="true" />} Confirmar y guardar
          </button>
        </div>
      </div>
    );
  } else if (pasoVisible === "listo") {
    cuerpo = (
      <div className="space-y-5">
        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-12 flex flex-col items-center gap-3 text-center">
          <CheckCircle2 size={52} className="text-emerald-600" aria-hidden="true" />
          <h1 className="text-2xl font-bold text-slate-900">Tu base está al día</h1>
          <p className="text-sm text-slate-600 max-w-md">Quedó guardada en el historial como la versión en uso, y el público se está actualizando con ella.</p>
          {carga?.revision && (
            <dl className="mt-3 grid w-full max-w-xl grid-cols-2 sm:grid-cols-4 gap-3 text-left" data-testid="lo-guardado">
              {numerosResumen(carga.revision.resumen).map((n) => (
                <div key={n.etiqueta} className="rounded-xl bg-slate-50 px-3 py-2.5">
                  <dt className="text-xs text-slate-500">{n.etiqueta}</dt>
                  <dd className="text-lg font-bold text-slate-900 tabular-nums">{n.valor}</dd>
                  {n.detalle && <dd className="text-xs text-slate-500">{n.detalle}</dd>}
                </div>
              ))}
            </dl>
          )}
        </div>
        {/* 29/10/2026: si la versión anterior tenía el consentimiento declarado, se pregunta si sigue valiendo */}
        {carga?.versionId && <DeclaracionEnListo versionId={carga.versionId} />}
        <div className="flex flex-wrap justify-end gap-3">
          <Link to={R_PUBLICO} className={btnSecundario}>Ver público</Link>
          <Link to={R_NUEVA_CAMPANA} className={btnPrimario}>Segmentar ahora</Link>
        </div>
      </div>
    );
  } else {
    const deshabilitada = !!ocupado;
    cuerpo = (
      <div className="space-y-6">
        <Encabezado antetitulo={inicio.hayBase ? "Actualizar base" : "Tu base de ventas"} titulo="Subí tus ventas">
          <p className="text-sm sm:text-base text-slate-600">
            {inicio.hayBase ? "El mismo export de siempre. Reemplaza a la versión anterior." : "Las ventas y los clientes que exportás de tu sistema."}
          </p>
        </Encabezado>

        <div role="radiogroup" aria-label="De dónde vienen tus datos" className="flex flex-wrap gap-3">
          <div role="radio" aria-checked="true" tabIndex={0}
            className="flex-1 basis-[260px] flex items-center gap-3.5 rounded-2xl border-2 border-violet-500 bg-violet-50/40 p-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400">
            <span className="w-10 h-10 rounded-xl bg-violet-100 text-violet-700 flex items-center justify-center" aria-hidden="true"><Upload size={18} /></span>
            <span className="flex flex-col"><span className="font-semibold text-slate-900">Subir archivo</span><span className="text-sm text-slate-600">Excel o CSV</span></span>
          </div>
          <div role="radio" aria-checked="false" aria-disabled="true" tabIndex={-1}
            className="flex-1 basis-[260px] flex items-center gap-3.5 rounded-2xl border border-slate-200 bg-white p-4 opacity-75">
            <span className="w-10 h-10 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center" aria-hidden="true"><Link2 size={18} /></span>
            <span className="flex flex-col"><span className="font-semibold text-slate-900">Conectar tu sistema</span><span className="text-sm text-slate-600">Se actualiza solo</span></span>
            <span className="ml-auto rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-500">Próximamente</span>
          </div>
        </div>

        {errorCarga && <p role="alert" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{errorCarga}</p>}

        <div className="flex flex-wrap gap-4">
          <ZonaArchivo titulo="Ventas" local={locales.ventas} servidor={carga?.archivos.ventas} deshabilitada={deshabilitada}
            ayuda={<>Necesita fecha <strong>y hora</strong>, cliente y monto (y el cupón, si lo tiene).</>}
            onArchivo={(f) => void elegir("ventas", f)}
            onReintentar={locales.ventas?.error ? () => reintentar("ventas") : undefined} />
          <ZonaArchivo titulo="Clientes" local={locales.clientes} servidor={carga?.archivos.clientes} deshabilitada={deshabilitada}
            ayuda="Los mismos clientes de las ventas, con su mail o su celular."
            onArchivo={(f) => void elegir("clientes", f)}
            onReintentar={locales.clientes?.error ? () => reintentar("clientes") : undefined} />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          {carga ? (
            <button type="button" onClick={() => void empezarDeNuevo()} disabled={deshabilitada} className="min-h-[44px] text-sm font-semibold text-slate-600 hover:text-slate-900 disabled:opacity-50">
              Empezar de nuevo
            </button>
          ) : <span />}
          <button type="button" onClick={() => void continuar()} disabled={!archivosCompletos(carga) || subiendo || deshabilitada} className={btnPrimario}>
            {ocupado === "leyendo" && <Loader2 size={15} className="animate-spin" aria-hidden="true" />} Continuar
          </button>
        </div>
      </div>
    );
  }

  return (
    <Pagina angosta>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to={R_PUBLICO} className="min-h-[44px] inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900">
          <ArrowLeft size={16} aria-hidden="true" /> Público
        </Link>
        <Pasos actual={INDICE[pasoVisible]} />
      </div>
      <MarcaDeTrabajo accion={pasoVisible === "listo" ? "Guardaste la base de" : "Subiendo la base de"} />
      {aviso && <p role="alert" className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-900">{aviso}</p>}
      {cuerpo}
    </Pagina>
  );
}
